/** Tela pública do clique no link de confirmação (SPEC-IDN-014).
 *
 * A pessoa não faz nada aqui: abrir a página **é** a confirmação. Pedir um
 * botão "confirmar" depois de ela já ter clicado no link do e-mail seria pedir
 * o mesmo consentimento duas vezes.
 *
 * Link inválido, vencido, já usado, superado por um mais novo e emitido para
 * outro endereço recebem a mesma mensagem — distinguir contaria a quem tenta um
 * link qualquer se ele um dia existiu.
 *
 * Em modo estrito o efeito monta, desmonta e monta de novo, e o token serve uma
 * vez só: a segunda chamada seria recusada e apagaria o sucesso da primeira.
 * Por isso o que se guarda no `useRef` é **a promessa**, e não um "já pedi" —
 * com o segundo, a montagem sobrevivente não chamaria a API e também não teria
 * a quem escutar, e a tela ficaria em "Confirmando…" para sempre. Guardando a
 * promessa, cada montagem escuta a mesma requisição.
 *
 * `useRef` e não `let` de módulo: um módulo valeria para a aplicação inteira, e
 * o segundo teste do arquivo já receberia a resposta do primeiro.
 */

import { useEffect, useRef, useState } from "react";

import { Botao } from "@/shared/ui/Botao";

import { confirmarEmail, type EmailConfirmado } from "./api";

type Estado =
  { situacao: "confirmando" } | { situacao: "pronto"; email: string } | { situacao: "invalido" };

interface Props {
  token: string;
  /** Para onde ir depois. Quem monta a aplicação decide. */
  aoSeguir: () => void;
}

export function ConfirmarEmail({ token, aoSeguir }: Props) {
  const [estado, setEstado] = useState<Estado>({ situacao: "confirmando" });
  const pedido = useRef<{ token: string; promessa: Promise<EmailConfirmado> } | null>(null);

  useEffect(() => {
    let vivo = true;
    if (pedido.current?.token !== token) {
      pedido.current = { token, promessa: confirmarEmail(token) };
    }

    pedido.current.promessa
      .then((resposta) => {
        if (vivo) setEstado({ situacao: "pronto", email: resposta.email });
      })
      .catch(() => {
        if (vivo) setEstado({ situacao: "invalido" });
      });
    return () => {
      vivo = false;
    };
  }, [token]);

  if (estado.situacao === "confirmando") {
    return (
      <Moldura>
        <p role="status" className="font-texto text-body text-texto-suave">
          Confirmando seu e-mail…
        </p>
      </Moldura>
    );
  }

  if (estado.situacao === "invalido") {
    return (
      <Moldura>
        <h1 className="font-display text-display-md text-texto">Link indisponível</h1>
        <p className="font-texto text-body text-texto-suave">
          Este link não vale mais. Isso acontece quando ele passou de 24 horas, já foi usado ou
          houve um pedido mais novo. Entre na sua conta e peça o envio outra vez.
        </p>
        <Botao larguraTotal onClick={aoSeguir}>
          Ir para o Tanzuh
        </Botao>
      </Moldura>
    );
  }

  return (
    <Moldura>
      <h1 className="font-display text-display-md text-texto">E-mail confirmado</h1>
      <p className="font-texto text-body text-texto-suave">
        Pronto: <strong className="text-texto">{estado.email}</strong> está confirmado. É para lá
        que enviaremos recuperação de senha e os avisos do seu dia.
      </p>
      <Botao larguraTotal onClick={aoSeguir}>
        Ir para o Tanzuh
      </Botao>
    </Moldura>
  );
}

function Moldura({ children }: { children: React.ReactNode }) {
  return (
    <main className="grid min-h-dvh place-items-center bg-canvas px-6">
      <div className="flex w-full max-w-[420px] flex-col gap-6">{children}</div>
    </main>
  );
}
